import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Mail, Lock, Loader2, Truck, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginDriver } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('client');
  const [emailNotVerified, setEmailNotVerified] = useState(null); // Store email for resend
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [branding, setBranding] = useState({
    company_name: 'Baobab Connect',
    logo_url: null,
    login_background_url: null,
    login_background_type: 'gradient',
    primary_color: '#1db584'
  });
  
  // Configuration des inscriptions
  const [registrationConfig, setRegistrationConfig] = useState({
    client_registration_enabled: true,
    driver_registration_enabled: true,
    registration_closed_message: ''
  });
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [driverData, setDriverData] = useState({
    emailOrPhone: '',
    password: ''
  });

  // Charger les paramètres de branding et inscriptions (endpoints publics)
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/system-config/branding/public`);
        if (response.ok) {
          const data = await response.json();
          logger.debug('Branding loaded:', data);
          setBranding(prev => ({
            ...prev,
            ...data
          }));
        }
      } catch (error) {
        logger.debug('Using default branding:', error);
      }
    };
    
    const fetchRegistrationConfig = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/system-config/registration/public`);
        if (response.ok) {
          const data = await response.json();
          logger.debug('Registration config loaded:', data);
          setRegistrationConfig(data);
        }
      } catch (error) {
        logger.debug('Using default registration config:', error);
      }
    };
    
    fetchBranding();
    fetchRegistrationConfig();
  }, []);

  // Vérifier si le chauffeur vient de s'inscrire
  useEffect(() => {
    if (searchParams.get('pending') === 'driver') {
      setActiveTab('driver');
      toast({
        title: "Inscription en attente",
        description: "Votre compte chauffeur est en attente de validation par l'administrateur.",
      });
    }
    
    // Vérifier si l'utilisateur vient de s'inscrire (nouveau flux avec validation email)
    if (searchParams.get('registered') === 'true') {
      const email = searchParams.get('email');
      toast({
        title: "📧 Vérifiez votre email",
        description: `Un email de confirmation a été envoyé à ${email || 'votre adresse'}. Cliquez sur le lien pour activer votre compte.`,
        duration: 10000
      });
      // Pre-fill email field
      if (email) {
        setFormData(prev => ({ ...prev, email: decodeURIComponent(email) }));
      }
    }
  }, [searchParams]);

  // Construire l'URL complète pour les images
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Déterminer le style de fond
  const getBackgroundStyle = () => {
    if (branding.login_background_type === 'image' && branding.login_background_url) {
      return {
        backgroundImage: `url(${getImageUrl(branding.login_background_url)})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    // Dégradé par défaut avec la couleur primaire
    const color = branding.primary_color || '#1db584';
    return {
      background: `linear-gradient(to bottom right, ${color}, ${adjustColor(color, -20)}, ${adjustColor(color, -40)})`
    };
  };

  // Fonction pour ajuster la luminosité d'une couleur
  const adjustColor = (color, amount) => {
    const hex = color.replace('#', '');
    const num = parseInt(hex, 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + amount));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
    return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleDriverChange = (e) => {
    setDriverData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setEmailNotVerified(null);
    setResendSuccess(false);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast({
        title: "✅ Connexion réussie",
        description: "Bienvenue sur Baobab Connect"
      });
      
      // Redirect based on role (RBAC)
      if (result.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (result.role === 'chauffeur' || result.role === 'driver') {
        navigate('/chauffeur/dashboard');
      } else {
        // expediteur, client, or any other role
        navigate('/dashboard');
      }
    } else {
      // Handle email not verified case
      if (result.error === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(result.email || formData.email);
        toast({
          title: "📧 Vérification requise",
          description: "Veuillez valider votre adresse email pour activer votre compte.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erreur de connexion",
          description: result.error,
          variant: "destructive"
        });
      }
    }
    
    setIsLoading(false);
  };

  // Function to resend verification email
  const handleResendVerification = async () => {
    if (!emailNotVerified) return;
    
    setResendLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailNotVerified })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResendSuccess(true);
        toast({
          title: "✅ Email envoyé",
          description: "Un nouveau lien de vérification a été envoyé à votre adresse email."
        });
      } else {
        toast({
          title: "Erreur",
          description: data.detail || "Erreur lors de l'envoi de l'email",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de l'envoi de l'email",
        variant: "destructive"
      });
    }
    setResendLoading(false);
  };
  
  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await loginDriver(driverData.emailOrPhone, driverData.password);
    
    if (result.success) {
      toast({
        title: "✅ Connexion réussie",
        description: "Bienvenue sur l'espace Chauffeur"
      });
      navigate('/chauffeur/dashboard');
    } else {
      toast({
        title: "Erreur de connexion",
        description: result.error,
        variant: "destructive"
      });
    }
    
    setIsLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-start justify-center pt-8 pb-8 px-4 md:items-center md:pt-0"
      style={getBackgroundStyle()}
    >
      {/* Overlay sombre pour les images de fond */}
      {branding.login_background_type === 'image' && branding.login_background_url && (
        <div className="absolute inset-0 bg-black/30" />
      )}
      
      <Card className="w-full max-w-md shadow-2xl relative z-10">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            {branding.logo_url ? (
              <img 
                src={getImageUrl(branding.logo_url)} 
                alt={branding.company_name}
                className="h-20 w-auto object-contain"
              />
            ) : (
              <div 
                className="p-3 rounded-xl shadow-md"
                style={{ background: `linear-gradient(to bottom right, ${branding.primary_color}, ${adjustColor(branding.primary_color, -20)})` }}
              >
                <Package className="w-8 h-8 text-white" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">{branding.company_name}</CardTitle>
          <CardDescription>Connectez-vous à votre espace</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="client" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Expéditeur
              </TabsTrigger>
              <TabsTrigger value="driver" className="flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Chauffeur
              </TabsTrigger>
            </TabsList>
            
            {/* Client/Admin Login */}
            <TabsContent value="client">
              <form onSubmit={handleClientSubmit} className="space-y-4">
                {/* Email not verified banner */}
                {emailNotVerified && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-yellow-800">
                          Veuillez valider votre adresse email
                        </p>
                        <p className="text-yellow-700 mt-1">
                          Votre compte n'est pas encore activé. Cliquez sur le lien dans l'email que nous vous avons envoyé.
                        </p>
                        {resendSuccess ? (
                          <p className="text-green-700 mt-2 font-medium">
                            ✅ Un nouveau lien a été envoyé à {emailNotVerified}
                          </p>
                        ) : (
                          <Button
                            type="button"
                            variant="link"
                            className="text-yellow-700 hover:text-yellow-800 p-0 h-auto mt-2 font-medium"
                            onClick={handleResendVerification}
                            disabled={resendLoading}
                          >
                            {resendLoading ? (
                              <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Envoi en cours...</>
                            ) : (
                              <>📧 Renvoyer l'email de vérification</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Mot de passe</Label>
                    <Link to="/forgot-password" className="text-xs text-[#1db584] hover:text-[#16a075] hover:underline">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full text-white"
                  style={{ 
                    background: `linear-gradient(to right, ${branding.primary_color}, ${adjustColor(branding.primary_color, -20)})` 
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connexion...</>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>
            </TabsContent>
            
            {/* Driver Login */}
            <TabsContent value="driver">
              <form onSubmit={handleDriverSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="emailOrPhone">Email ou Téléphone</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="emailOrPhone"
                      name="emailOrPhone"
                      type="text"
                      placeholder="email@example.com ou +225..."
                      value={driverData.emailOrPhone}
                      onChange={handleDriverChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="driverPassword">Mot de passe</Label>
                    <Link to="/forgot-password?type=driver" className="text-xs text-[#1db584] hover:text-[#16a075] hover:underline">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="driverPassword"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={driverData.password}
                      onChange={handleDriverChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                
                {/* Pending validation notice */}
                {searchParams.get('pending') === 'driver' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      Votre compte est en attente de validation. Vous recevrez un email une fois approuvé.
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#1a1a2e] hover:bg-[#16213e] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connexion...</>
                  ) : (
                    'Se connecter'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Lien d'inscription - conditionnel selon la configuration */}
          {(activeTab === 'client' && registrationConfig.client_registration_enabled) || 
           (activeTab === 'driver' && registrationConfig.driver_registration_enabled) ? (
            <p className="text-center text-sm text-gray-600 mt-6">
              Pas encore de compte ?{' '}
              <Link to="/inscription" className="text-[#1db584] hover:text-[#16a075] font-semibold hover:underline">
                S&apos;inscrire
              </Link>
            </p>
          ) : null}
          
          {/* Bouton de suivi d'opération */}
          <div className="mt-6 pt-6 border-t">
            <Link to="/public/suivi">
              <Button 
                variant="outline" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:border-blue-700 font-semibold shadow-sm"
              >
                <Package className="w-4 h-4 mr-2" />
                Suivre mon opération
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
